import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { showActivity, hideActivity } from '../activity';
import { API } from '../config';
import { notifyError, notify } from '../notify';


function Vault({ selectedCreator, role }) {
  // Active spoof-test pollers — cleared on unmount so intervals don't
  // keep firing after the user leaves the page
  const activePollersRef = useRef([]);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    const pollers = activePollersRef.current;
    return () => {
      mountedRef.current = false;
      for (const p of pollers) {
        clearInterval(p.interval);
        clearTimeout(p.timeout);
      }
      pollers.length = 0;
      // Don't leave a "please stay on this page" toast behind
      hideActivity();
    };
  }, []);
  // Stops one poller (interval + its timeout) and forgets it
  const stopPoller = (interval, timeout) => {
    clearInterval(interval);
    clearTimeout(timeout);
    activePollersRef.current = activePollersRef.current.filter(p => p.interval !== interval);
  };
  // Request counters: responses for an older creator / folder are dropped
  const itemsSeqRef = useRef(0);
  const foldersSeqRef = useRef(0);

  const [items, setItems] = useState([]);
  const [contentTags, setContentTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Filters & view
  const [statusFilter, setStatusFilter] = useState('available');
  const [tagFilter, setTagFilter] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date_new');
  const [mediaFilter, setMediaFilter] = useState('all');

  // Upload
  const [showUpload, setShowUpload] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadFolder, setUploadFolder] = useState('');
  const [uploadTags, setUploadTags] = useState([]);
  const [uploadTitle, setUploadTitle] = useState('');
  const [editingFileIndex, setEditingFileIndex] = useState(null);
  const fileInputRef = useRef(null);
  const [displayCount, setDisplayCount] = useState(50);
  const [batchSize, setBatchSize] = useState(50);

  // Select mode
  const [selectMode, setSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [brightnessPct, setBrightnessPct] = useState(15);
  const [detailZoom, setDetailZoom] = useState(false); // click-to-enlarge in the detail modal
  const [imgBust, setImgBust] = useState(0); // cache-bust after in-place brighten
  const [bulkDescPresetId, setBulkDescPresetId] = useState('');

  // Tag management
  const [showTagManager, setShowTagManager] = useState(false);
  // Latest thumbnail check (one runs at a time for the whole app)
  const [thumbRun, setThumbRun] = useState(null);
  const [showThumbReport, setShowThumbReport] = useState(false);
  const thumbActive = thumbRun && (thumbRun.status === 'running' || thumbRun.status === 'queued');
  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/api/vault/thumbnail-check/status`)
      .then(r => { if (alive) setThumbRun(r.data?.run || null); }).catch(() => {});
    load();
    const t = setInterval(() => { if (thumbActive) load(); }, 5000); // follow a running check
    return () => { alive = false; clearInterval(t); };
  }, [thumbActive]);
  const [newTagName, setNewTagName] = useState('');

  // Detail modal
  // Folders
  const [folders, setFolders] = useState([]);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [activeFolder, setActiveFolder] = useState(null); // null = all, 'unfiled' = no folder, uuid = specific folder
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renamingFolderValue, setRenamingFolderValue] = useState('');
  const [folderMenuOpen, setFolderMenuOpen] = useState(null);
  const [folderSelectMode, setFolderSelectMode] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [lastVisitedFolder, setLastVisitedFolder] = useState(null);
  const [folderViewMode, setFolderViewMode] = useState('grid');
  const [folderSortBy, setFolderSortBy] = useState('date_new');
  const [showUsageHistory, setShowUsageHistory] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailHistory, setDetailHistory] = useState([]);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingNotes, setEditingNotes] = useState('');
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkTitle, setBulkTitle] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkTagsAdd, setBulkTagsAdd] = useState([]);
  const [bulkTagsRemove, setBulkTagsRemove] = useState([]);
  const [descPresets, setDescPresets] = useState([]);
  const [selectedDescPresetId, setSelectedDescPresetId] = useState('');
  const [showDescSettings, setShowDescSettings] = useState(false);
  const [descPresetForm, setDescPresetForm] = useState({ name: '', custom_prompt: '' });
  const [showDescPresetForm, setShowDescPresetForm] = useState(false);
  const [editingDescPreset, setEditingDescPreset] = useState(null);

  const creatorId = selectedCreator?.id;
  // Latest folder/creator for async callbacks (pollers) started earlier
  const viewRef = useRef({ creatorId, activeFolder });
  viewRef.current = { creatorId, activeFolder };
  //redep

  useEffect(() => {
    // Creator switched → folders, selection and upload target belonged to the
    // previous creator; start clean (and ignore their in-flight responses).
    itemsSeqRef.current++;
    foldersSeqRef.current++;
    setItems([]);
    setFolders([]);
    setUnfiledCount(0);
    setActiveFolder(null);
    setLastVisitedFolder(null);
    setSelectMode(false);
    setSelectedItems([]);
    setFolderSelectMode(false);
    setSelectedFolders([]);
    setFolderMenuOpen(null);
    setShowMoveModal(false);
    setBulkEditOpen(false);
    setDetailItem(null);
    setUploadFolder('');
    setPendingFiles(prev => prev.map(f => ({ ...f, folder_id: null })));
    fetchContentTags();
    if (selectedCreator?.id) {
      fetchFolders();
      fetchDescPresets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCreator?.id]);

  const fetchDescPresets = async () => {
    if (!selectedCreator?.id) return;
    try {
      const res = await axios.get(`${API}/api/joycaption-presets/${selectedCreator.id}`);
      setDescPresets(res.data);
    } catch (err) {
      notifyError(err, 'Could not load description presets');
    }
  };

  useEffect(() => {
    if (!creatorId) return;
    if (statusFilter !== 'available') {
      fetchItemsFlat();
    } else if (activeFolder !== null) {
      fetchItems(activeFolder);
    } else {
      itemsSeqRef.current++; // folder overview: drop any in-flight item list
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId, statusFilter, tagFilter, activeFolder]);
////comment 
  useEffect(() => {
    setDisplayCount(batchSize); // Reset visible count when filters change
  }, [statusFilter, tagFilter, search, sortBy, mediaFilter, activeFolder, batchSize]);

  useEffect(() => { setDetailZoom(false); }, [detailItem?.id]); // reset zoom when opening a new item

  const fetchItems = async (folderId) => {
    if (!creatorId) return;
    const folder = folderId !== undefined ? folderId : activeFolder;
    const seq = ++itemsSeqRef.current;
    if (folder === null) { setItems([]); return; }
    setLoading(true);
    try {
      let url = `${API}/api/vault/creator/${creatorId}?`;
      if (folder === 'unfiled') url += 'folder_id=unfiled&';
      else if (folder) url += `folder_id=${folder}&`;
      if (tagFilter.length > 0) url += `tags=${tagFilter.join(',')}&`;
      url = url.replace(/[&?]$/, '');
      const res = await axios.get(url);
      if (seq !== itemsSeqRef.current) return;
      setItems(res.data);
    } catch (err) {
      if (seq !== itemsSeqRef.current) return;
      notifyError(err, 'Could not load vault items');
    }
    setLoading(false);
  };

  const fetchItemsFlat = async () => {
    if (!creatorId) return;
    const seq = ++itemsSeqRef.current;
    setLoading(true);
    try {
      let url = `${API}/api/vault/creator/${creatorId}?status=${statusFilter}`;
      if (tagFilter.length > 0) url += `&tags=${tagFilter.join(',')}`;
      const res = await axios.get(url);
      if (seq !== itemsSeqRef.current) return;
      setItems(res.data);
    } catch (err) {
      if (seq !== itemsSeqRef.current) return;
      notifyError(err, 'Could not load vault items');
    }
    setLoading(false);
  };

 ///comment
  const fetchFolders = async () => {
    if (!creatorId) return;
    const seq = ++foldersSeqRef.current;
    try {
      const res = await axios.get(`${API}/api/vault/folders/${creatorId}`);
      if (seq !== foldersSeqRef.current) return;
      setFolders(res.data.folders || []);
      setUnfiledCount(res.data.unfiled_count || 0);
    } catch (err) {
      if (seq !== foldersSeqRef.current) return;
      notifyError(err, 'Could not load folders');
    }
  };

  const fetchContentTags = async () => {
    try {
      const res = await axios.get(`${API}/api/vault/content-tags`);
      setContentTags(res.data);
    } catch (err) {
      notifyError(err, 'Could not load content tags');
    }
  };

  const fetchItemHistory = async (id) => {
    try {
      const res = await axios.get(`${API}/api/vault/${id}/history`);
      setDetailHistory(res.data);
    } catch (err) {
      notifyError(err, 'Could not load usage history');
    }
  };

  // ─── Upload Handlers ───

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const newPending = files.map(f => ({
      file: f,
      preview: URL.createObjectURL(f),
      title: '',
      notes: '',
      tags: [],
      folder_id: uploadFolder || null
    }));
    setPendingFiles(prev => [...prev, ...newPending]);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (uploading) return;
    const files = Array.from(e.dataTransfer.files).filter(f =>
      f.type.startsWith('image/') || f.type === 'video/mp4'
    );
    if (!files.length) return;
    const newPending = files.map(f => ({
      file: f,
      preview: URL.createObjectURL(f),
      title: '',
      notes: '',
      tags: [],
      folder_id: uploadFolder || null
    }));
    setPendingFiles(prev => [...prev, ...newPending]);
    setUploadError('');
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
    if (editingFileIndex === index) setEditingFileIndex(null);
    else if (editingFileIndex > index) setEditingFileIndex(editingFileIndex - 1);
  };

  const updatePendingFile = (index, field, value) => {
    setPendingFiles(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
  };

  const togglePendingFileTag = (index, tagName) => {
    setPendingFiles(prev => prev.map((f, i) => {
      if (i !== index) return f;
      const tags = f.tags.includes(tagName) ? f.tags.filter(t => t !== tagName) : [...f.tags, tagName];
      return { ...f, tags };
    }));
  };

  const handleUploadAll = async () => {
    if (!pendingFiles.length || !creatorId) return;
    setUploading(true);
    setUploadError('');
    showActivity(`Uploading 1 of ${pendingFiles.length} files...`);
    const queue = [...pendingFiles];
    const failed = [];
    let lastError = null;

    for (let n = 0; n < queue.length; n++) {
      const pending = queue[n];
      try {
        if (mountedRef.current) showActivity(`Uploading ${n + 1} of ${queue.length}...`);
        const formData = new FormData();
        formData.append('files', pending.file);
        formData.append('creator_id', creatorId);
        if (pending.title) formData.append('title_suggestion', pending.title);
        if (pending.notes) formData.append('notes', pending.notes);
        if (pending.folder_id) formData.append('folder_id', pending.folder_id);

        const res = await axios.post(`${API}/api/vault/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const uploadedItem = res.data?.uploaded?.[0];
        if (uploadedItem && pending.tags.length > 0) {
          await axios.put(`${API}/api/vault/${uploadedItem.id}/tags`, { content_tags: pending.tags });
        }
      } catch (err) {
        console.error(`Upload error for ${pending.file.name}:`, err);
        failed.push(pending);
        lastError = err;
      }
    }

    // Keep the failed files in the queue so they can be retried; drop the
    // uploaded ones (and files added while uploading stay too).
    const done = new Set(queue.filter(p => !failed.includes(p)));
    done.forEach(f => URL.revokeObjectURL(f.preview));
    setPendingFiles(prev => prev.filter(p => !done.has(p)));
    setEditingFileIndex(null);
    setUploading(false);
    hideActivity();

    if (failed.length > 0) {
      setUploadError(`${failed.length} file${failed.length > 1 ? 's' : ''} failed to upload — still in the list, click Upload to retry.`);
      notifyError(lastError, `${failed.length} upload${failed.length > 1 ? 's' : ''} failed`);
    } else {
      setUploadFolder('');
      setUploadTags([]);
      setUploadTitle('');
      setShowUpload(false);
    }

    if (!mountedRef.current) return;
    fetchItems();
    fetchFolders();
  };

  // ─── Item Actions ───

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/api/vault/${id}/status`, { status });
      setItems(prev => prev.map(item => item.id === id ? { ...item, status } : item));
      if (detailItem?.id === id) setDetailItem(prev => ({ ...prev, status }));
    } catch (err) {
      notifyError(err, 'Could not update status');
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/api/vault/${id}`);
      setItems(prev => prev.filter(item => item.id !== id));
      if (detailItem?.id === id) setDetailItem(null);
    } catch (err) {
      // Surface the failure — this used to be swallowed, so a blocked delete
      // looked like the button simply "doing nothing".
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedItems.length} items permanently?`)) return;
    showActivity(`Deleting ${selectedItems.length} items...`);
    try {
      await axios.post(`${API}/api/vault/bulk-delete`, { ids: selectedItems });
      setItems(prev => prev.filter(item => !selectedItems.includes(item.id)));
      setSelectedItems([]);
      fetchFolders();
      setSelectMode(false);
      fetchFolders();
      hideActivity();
    } catch (err) {
      hideActivity();
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  };

  // Brighten (+) or darken (-) selected images by brightnessPct, with the
  // standard progress popup, then refresh the folder in place.
  // direction: +1 brighten / -1 darken by brightnessPct. idsOverride lets the
  // single detail-modal use the exact same logic as the bulk selection.
  const applyBrightness = async (direction, idsOverride = null) => {
    const isSingle = !!idsOverride;
    const imageIds = (idsOverride || selectedItems).filter(id => {
      const it = items.find(i => i.id === id);
      return it && it.media_type !== 'gif' && it.media_type !== 'video';
    });
    if (imageIds.length === 0) { alert('No images to adjust (GIFs/videos are skipped).'); return; }
    const verb = direction > 0 ? 'Brighten' : 'Darken';
    if (!window.confirm(`${verb} ${imageIds.length} image${imageIds.length !== 1 ? 's' : ''} by ${brightnessPct}%?\n\nThis edits the original in place.`)) return;

    const multiplier = direction > 0 ? 1 + brightnessPct / 100 : 1 - brightnessPct / 100;
    if (!isSingle) { setSelectMode(false); setSelectedItems([]); }
    showActivity(`${verb}ing ${imageIds.length} image${imageIds.length !== 1 ? 's' : ''}…`);
    try {
      const batchSize = 10;
      let done = 0;
      for (let i = 0; i < imageIds.length; i += batchSize) {
        const batch = imageIds.slice(i, i + batchSize);
        await axios.post(`${API}/api/vault/brighten`, { vault_ids: batch, brightness: multiplier });
        done += batch.length;
        showActivity(`${verb}ing… ${done}/${imageIds.length}`);
      }
      await fetchItems(activeFolder); // refresh grid with regenerated thumbnails
      if (isSingle) setImgBust(Date.now()); // brighten edits in place — bust cache so the preview updates
      hideActivity();
    } catch (err) {
      hideActivity();
      alert(`${verb} failed: ` + (err.response?.data?.error || err.message));
    }
  };

  // ── Folder multi-select actions ──
  const handleFolderClick = (folder) => {
    if (folderSelectMode) {
      setSelectedFolders(prev => prev.includes(folder.id) ? prev.filter(x => x !== folder.id) : [...prev, folder.id]);
    } else {
      setActiveFolder(folder.id); setDisplayCount(batchSize); setSelectMode(false); setSelectedItems([]);
    }
  };

  const fetchFolderItems = async (folderId) => {
    const res = await axios.get(`${API}/api/vault/creator/${creatorId}?folder_id=${folderId}`);
    return res.data || [];
  };

  // Describe images/GIFs across the selected folders.
  // overwrite=false → only fill in missing descriptions.
  // overwrite=true  → rewrite every image/GIF (e.g. to apply an improved prompt).
  const describeFolders = async (overwrite = false) => {
    if (selectedFolders.length === 0) return;
    const preset = descPresets.find(p => p.id === bulkDescPresetId);
    showActivity('Scanning folders…');
    let targets = []; // { id, oldDesc }
    try {
      for (const fid of selectedFolders) {
        const items = await fetchFolderItems(fid);
        for (const i of items) {
          if (i.media_type === 'video') continue;
          if (!overwrite && i.image_description) continue;
          targets.push({ id: i.id, oldDesc: i.image_description || '' });
        }
      }
    } catch (err) { hideActivity(); notifyError(err, 'Error reading folders'); return; }
    const seenIds = new Set();
    targets = targets.filter(t => seenIds.has(t.id) ? false : (seenIds.add(t.id), true));
    if (targets.length === 0) {
      hideActivity();
      alert(overwrite ? 'No images or GIFs in the selected folders.' : 'Everything already has a description. Use "Re-describe all" to rewrite them.');
      return;
    }

    const targetIds = targets.map(t => t.id);
    const oldMap = {}; targets.forEach(t => { oldMap[t.id] = t.oldDesc; });
    try {
      await axios.post(`${API}/api/vault/bulk-describe`, { vault_item_ids: targetIds, custom_prompt: preset?.custom_prompt || '', overwrite });
    } catch (err) { hideActivity(); alert('Describe failed: ' + (err.response?.data?.error || err.message)); return; }

    const folderIds = [...selectedFolders];
    setFolderSelectMode(false); setSelectedFolders([]);
    const targetSet = new Set(targetIds);
    const started = Date.now();
    const startedFor = creatorId;
    const verb = overwrite ? 'Re-describing' : 'Describing';
    showActivity(`${verb} ${targetIds.length} image${targetIds.length !== 1 ? 's' : ''} across ${folderIds.length} folder${folderIds.length !== 1 ? 's' : ''}…`);
    const poll = setInterval(async () => {
      try {
        let done = 0;
        for (const fid of folderIds) {
          const items = await fetchFolderItems(fid);
          for (const i of items) {
            // done = has a description that differs from the old one
            if (targetSet.has(i.id) && i.image_description && i.image_description !== oldMap[i.id]) done++;
          }
        }
        showActivity(`${verb}… ${done}/${targetIds.length} done`);
        if (done >= targetIds.length || Date.now() - started > 300000) {
          stopPoller(poll, timeoutId); hideActivity();
          // refresh only if the user is still looking at the same creator/folder
          if (viewRef.current.creatorId === startedFor) {
            fetchFolders();
            if (activeFolder && viewRef.current.activeFolder === activeFolder) fetchItems(activeFolder);
          }
        }
      } catch (e) { /* keep polling */ }
    }, 4000);
    const timeoutId = setTimeout(() => { stopPoller(poll, timeoutId); hideActivity(); }, 305000);
    activePollersRef.current.push({ interval: poll, timeout: timeoutId });
  };

  const renameSelectedFolder = async () => {
    if (selectedFolders.length !== 1) return;
    const folder = folders.find(f => f.id === selectedFolders[0]);
    if (!folder) return;
    const name = prompt('Rename folder:', folder.name || '');
    if (!name || !name.trim() || name.trim() === folder.name) return;
    try {
      await axios.put(`${API}/api/vault/folders/${folder.id}`, { name: name.trim() });
      fetchFolders();
      setFolderSelectMode(false);
      setSelectedFolders([]);
    } catch (err) { alert('Rename failed: ' + (err.response?.data?.error || err.message)); }
  };

  const deleteFolders = async (withContent) => {
    if (selectedFolders.length === 0) return;
    const names = folders.filter(f => selectedFolders.includes(f.id)).map(f => f.name);
    const msg = withContent
      ? `Delete ${selectedFolders.length} folder(s) AND all items inside permanently?\n\n${names.slice(0, 8).join(', ')}${names.length > 8 ? '…' : ''}\n\nThis cannot be undone.`
      : `Delete ${selectedFolders.length} folder(s)? Items inside become unfiled (kept).\n\n${names.slice(0, 8).join(', ')}${names.length > 8 ? '…' : ''}`;
    if (!window.confirm(msg)) return;
    showActivity(`Deleting ${selectedFolders.length} folder${selectedFolders.length !== 1 ? 's' : ''}…`);
    let failed = [];
    for (const fid of selectedFolders) {
      try {
        await axios.delete(`${API}/api/vault/folders/${fid}${withContent ? '/with-content' : ''}`);
      } catch (err) {
        failed.push(folders.find(f => f.id === fid)?.name || fid);
      }
    }
    hideActivity();
    if (failed.length) alert(`Some folders couldn't be deleted (likely have subfolders): ${failed.join(', ')}`);
    setFolderSelectMode(false); setSelectedFolders([]);
    fetchFolders();
    if (activeFolder) fetchItems(activeFolder);
  };

  const bulkEdit = async () => {
    const succeeded = new Set();
    const failures = [];
    const updates = selectedItems.map(async (id) => {
      const item = items.find(i => i.id === id);
      if (!item) return;

      const changes = {};
      if (bulkTitle) changes.title_suggestion = bulkTitle;
      if (bulkNotes) changes.notes = bulkNotes;

      if (bulkTagsAdd.length > 0 || bulkTagsRemove.length > 0) {
        let currentTags = [...(item.content_tags || [])];
        bulkTagsAdd.forEach(t => { if (!currentTags.includes(t)) currentTags.push(t); });
        bulkTagsRemove.forEach(t => { currentTags = currentTags.filter(ct => ct !== t); });
        changes.content_tags = currentTags;
      }

      if (Object.keys(changes).length === 0) return;

      try {
        if (changes.content_tags) {
          await axios.put(`${API}/api/vault/${id}/tags`, { content_tags: changes.content_tags });
        }
        if (changes.title_suggestion || changes.notes) {
          await axios.put(`${API}/api/vault/${id}`, {
            title_suggestion: changes.title_suggestion || item.title_suggestion || '',
            notes: changes.notes || item.notes || ''
          });
        }
        succeeded.add(id);
      } catch (err) {
        failures.push({ id, err });
      }
    });

    await Promise.all(updates);

    // Update local state — only for the items the server accepted
    setItems(prev => prev.map(item => {
      if (!succeeded.has(item.id)) return item;
      const updated = { ...item };
      if (bulkTitle) updated.title_suggestion = bulkTitle;
      if (bulkNotes) updated.notes = bulkNotes;
      if (bulkTagsAdd.length > 0 || bulkTagsRemove.length > 0) {
        let currentTags = [...(updated.content_tags || [])];
        bulkTagsAdd.forEach(t => { if (!currentTags.includes(t)) currentTags.push(t); });
        bulkTagsRemove.forEach(t => { currentTags = currentTags.filter(ct => ct !== t); });
        updated.content_tags = currentTags;
      }
      return updated;
    }));

    if (failures.length > 0) {
      // Keep the dialog + selection on the failed items so they can be retried
      setSelectedItems(failures.map(f => f.id));
      notifyError(failures[0].err, `${failures.length} of ${failures.length + succeeded.size} items were not updated`);
      return;
    }
    setBulkEditOpen(false);
    setBulkTitle('');
    setBulkNotes('');
    setBulkTagsAdd([]);
    setBulkTagsRemove([]);
    if (succeeded.size) notify(`Updated ${succeeded.size} item${succeeded.size !== 1 ? 's' : ''}`, 'success', 3000);
  };

  const saveDetail = async () => {
    if (!detailItem) return;
    try {
      await axios.put(`${API}/api/vault/${detailItem.id}`, {
        title_suggestion: editingTitle,
        notes: editingNotes,
        content_tags: detailItem.content_tags || []
      });
      setItems(prev => prev.map(item =>
        item.id === detailItem.id ? { ...item, title_suggestion: editingTitle, notes: editingNotes } : item
      ));
      setDetailItem(prev => ({ ...prev, title_suggestion: editingTitle, notes: editingNotes }));
    } catch (err) {
      notifyError(err, 'Could not save item');
    }
  };

  const toggleItemTag = async (tag) => {
    if (!detailItem) return;
    const current = detailItem.content_tags || [];
    const updated = current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag];
    try {
      await axios.put(`${API}/api/vault/${detailItem.id}/tags`, { content_tags: updated });
      setDetailItem(prev => ({ ...prev, content_tags: updated }));
      setItems(prev => prev.map(item =>
        item.id === detailItem.id ? { ...item, content_tags: updated } : item
      ));
    } catch (err) {
      notifyError(err, 'Could not update tags');
    }
  };

  // ─── Tag Management ───

  const addContentTag = async () => {
    if (!newTagName.trim()) return;
    try {
      await axios.post(`${API}/api/vault/content-tags`, { name: newTagName.trim() });
      setNewTagName('');
      fetchContentTags();
    } catch (err) {
      notifyError(err, 'Could not add tag');
    }
  };

  const deleteContentTag = async (id) => {
    if (!window.confirm('Delete this tag? It will be removed from all content.')) return;
    try {
      await axios.delete(`${API}/api/vault/content-tags/${id}`);
      fetchContentTags();
      fetchItems();
    } catch (err) {
      notifyError(err, 'Could not delete tag');
    }
  };

  const toggleTagFilter = (tag) => {
    setTagFilter(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const toggleSelectItem = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  // Detail modal navigation
  const navigateDetail = (direction) => {
    if (!detailItem) return;
    const currentIndex = filtered.findIndex(i => i.id === detailItem.id);
    if (currentIndex === -1) return;
    const newIndex = direction === 'next'
      ? (currentIndex + 1) % filtered.length
      : (currentIndex - 1 + filtered.length) % filtered.length;
    const newItem = filtered[newIndex];
    if (newItem) {
      setDetailItem(newItem);
      setEditingTitle(newItem.title_suggestion || '');
      setEditingNotes(newItem.notes || '');
      setShowUsageHistory(false);
      setDetailHistory([]);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'available') return '#22c55e';
    if (status === 'drafted') return '#f59e0b';
    if (status === 'used') return '#3b82f6';
    if (status === 'rejected') return '#ff4500';
    return '#aaaaaa';
  };

  // Filter and paginate
  // Get subfolders of current folder
  const currentSubfolders = activeFolder && activeFolder !== 'unfiled'
    ? folders.filter(f => f.parent_id === activeFolder)
    : activeFolder === null
      ? folders.filter(f => !f.parent_id)
      : [];

  // Build breadcrumb path
  const getBreadcrumbPath = () => {
    if (!activeFolder || activeFolder === 'unfiled') return [];
    const folderMap = {};
    folders.forEach(f => { folderMap[f.id] = f; });
    const path = [];
    let current = folderMap[activeFolder];
    while (current && path.length < 10) {
      path.unshift(current);
      current = current.parent_id ? folderMap[current.parent_id] : null;
    }
    return path;
  };

  const breadcrumbPath = getBreadcrumbPath();

  const getMediaType = (filename) => {
    if (!filename) return 'image';
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'gif') return 'gif';
    if (['mp4', 'mov', 'webm', 'avi'].includes(ext)) return 'video';
    if (['jpg', 'jpeg', 'png', 'webp', 'bmp'].includes(ext)) return 'image';
    return 'other';
  };

  const filtered = items.filter(item => {
    // Status filter
    if (statusFilter === 'available') {
      if (item.status === 'rejected') return false; // hide rejected from available view
    }

    // Folder filter — only apply when viewing 'available' status
    if (statusFilter === 'available') {
      if (activeFolder === 'unfiled' && item.folder_id) return false;
      if (activeFolder && activeFolder !== 'unfiled' && item.folder_id !== activeFolder) return false;
    }

    // Media type filter
    if (mediaFilter !== 'all') {
      const type = getMediaType(item.original_filename);
      if (mediaFilter === 'images' && type !== 'image') return false;
      if (mediaFilter === 'gifs' && type !== 'gif') return false;
      if (mediaFilter === 'videos' && type !== 'video') return false;
    }

    // Search filter
    if (search) {
      return (item.original_filename || '').toLowerCase().includes(search.toLowerCase()) ||
        (item.title_suggestion || '').toLowerCase().includes(search.toLowerCase());
    }
    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'name_asc': return (a.title_suggestion || a.original_filename || '').localeCompare(b.title_suggestion || b.original_filename || '');
      case 'name_desc': return (b.title_suggestion || b.original_filename || '').localeCompare(a.title_suggestion || a.original_filename || '');
      case 'date_new': return new Date(b.created_at) - new Date(a.created_at);
      case 'date_old': return new Date(a.created_at) - new Date(b.created_at);
      case 'modified_new': return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
      case 'modified_old': return new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at);
      default: return 0;
    }
  });

  const displayed = filtered.slice(0, displayCount);
  const hasMore = displayCount < filtered.length;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Content Vault</h1>
          <p>{selectedCreator?.name || 'Creator'} — {activeFolder === null && statusFilter === 'available' ? `${folders.reduce((sum, f) => sum + (f.item_count || 0), 0) + unfiledCount} total items` : `${filtered.length} items${statusFilter !== 'available' ? ` (${statusFilter})` : ''}`}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-secondary" onClick={() => setShowDescSettings(true)}>
            👁️ AI Settings
          </button>
          <button className="btn-secondary" title="Re-extract the structured attributes (outfit, setting, face visible, shot type…) from the existing descriptions. Text-only; descriptions are not regenerated." onClick={async () => {
            if (!creatorId) return;
            const all = window.confirm('Refresh attributes for ALL described items (OK), or only fill items that have none yet (Cancel)?\n\nText-only, runs in the background for a few minutes.');
            try {
              const res = await axios.post(`${API}/api/vault/backfill-attributes`, { creator_id: creatorId, overwrite: all });
              alert(`🏷 Queued ${res.data.queued} items (${all ? 'all described items' : 'items without attributes'}).`);
            } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
          }}>🏷 Attributes</button>
          <button className="btn-secondary" disabled={!!thumbActive}
            title={thumbActive ? 'A thumbnail check is running — one at a time' : 'Check every thumbnail of this creator and rebuild the missing ones (runs in the background)'}
            onClick={async () => {
            if (!creatorId) return;
            if (!window.confirm('Check this creator\'s thumbnails and rebuild the missing ones?\n\nRuns in the background, gently (one creator at a time). Progress and a report appear here.')) return;
            try {
              const res = await axios.post(`${API}/api/vault/backfill-thumbnails`, { creator_id: creatorId });
              setThumbRun(res.data.run || null);
              setShowThumbReport(false);
            } catch (err) {
              if (err.response?.status === 409 && err.response.data?.run) setThumbRun(err.response.data.run);
              notifyError(err, 'Could not start the thumbnail check');
            }
          }}>🖼 {thumbActive ? 'Checking…' : 'Thumbnails'}</button>
          
          <button className="btn-secondary" onClick={() => setShowTagManager(!showTagManager)}>
            🏷 Tags {showTagManager ? '▲' : '▼'}
          </button>
          <button onClick={() => {
            const newShow = !showUpload;
            setShowUpload(newShow);
            if (newShow) {
              setUploadFolder(activeFolder && activeFolder !== 'unfiled' ? activeFolder : '');
            }
          }}>
            {showUpload ? 'Cancel' : '+ Upload'}
          </button>
        </div>
      </div>

      {/* Thumbnail check: live progress, then the report */}
      {thumbRun && !thumbRun.other_organisation && (thumbActive || thumbRun.creator_id === creatorId) && (
        <div style={{ margin: '0 0 12px', padding: '10px 14px', borderRadius: '10px', fontSize: '12px', lineHeight: 1.6,
          background: thumbActive ? '#0f1a2a' : (thumbRun.failed || thumbRun.busy) ? '#1a1408' : '#0a1a0f',
          border: `1px solid ${thumbActive ? '#1e3a5f' : (thumbRun.failed || thumbRun.busy) ? '#3f3410' : '#14532d'}`,
          color: thumbActive ? '#93c5fd' : '#ccc' }}>
          {thumbActive ? (
            <>⏳ <b>Checking thumbnails for {thumbRun.creator_name}</b>: {thumbRun.done || 0} of {thumbRun.total || '?'}
              {' '}· {thumbRun.fine || 0} fine · {thumbRun.regenerated || 0} rebuilt · {thumbRun.failed || 0} failed{thumbRun.busy ? ` · ${thumbRun.busy} not checked (storage busy)` : ''}</>
          ) : thumbRun.status === 'interrupted' ? (
            <>⚠ The thumbnail check for {thumbRun.creator_name} was interrupted (server restart) at {thumbRun.done || 0} of {thumbRun.total || '?'} — run it again.</>
          ) : (
            <>
              🖼 <b>Thumbnail check for {thumbRun.creator_name}</b>{thumbRun.finished_at ? ` (${new Date(thumbRun.finished_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })})` : ''}:
              {' '}<span style={{ color: '#22c55e' }}>{thumbRun.fine || 0} fine</span>
              {' '}· <span style={{ color: '#60a5fa' }}>{thumbRun.regenerated || 0} rebuilt</span>
              {' '}· <span style={{ color: thumbRun.failed ? '#ef4444' : '#888' }}>{thumbRun.failed || 0} failed</span>
              {thumbRun.busy ? <> · <span style={{ color: '#f59e0b' }}>{thumbRun.busy} not checked (storage busy — run again later)</span></> : null}
              {(thumbRun.failures || []).length > 0 && (
                <button className="btn-secondary" onClick={() => setShowThumbReport(v => !v)} style={{ marginLeft: '10px', fontSize: '11px', padding: '2px 8px' }}>
                  {showThumbReport ? 'hide' : 'show'} problem items
                </button>
              )}
              {showThumbReport && (
                <div style={{ marginTop: '6px', maxHeight: '180px', overflowY: 'auto', color: '#aaa' }}>
                  {(thumbRun.failures || []).map(f => <div key={f.id}>• {f.name} — {f.reason}</div>)}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Tag Manager */}
      {showTagManager && (
        <div className="card">
          <h2>Content Tag Library</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
            {contentTags.map(tag => (
              <span key={tag.id} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: '#2a2a2a', border: '1px solid #444', borderRadius: '20px',
                padding: '4px 12px', fontSize: '12px', color: '#ffffff'
              }}>
                {tag.name}
                <span
                  onClick={() => deleteContentTag(tag.id)}
                  style={{ cursor: 'pointer', color: '#ff4500', fontWeight: '700', fontSize: '14px' }}
                >×</span>
              </span>
            ))}
            {contentTags.length === 0 && <span style={{ color: '#555', fontSize: '13px' }}>No tags yet.</span>}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              placeholder="New tag name..."
              value={newTagName}
              onChange={e => setNewTagName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addContentTag()}
              style={{ flex: 1 }}
            />
            <button onClick={addContentTag} disabled={!newTagName.trim()}>Add Tag</button>
          </div>
        </div>
      )}

      {/* Upload Panel */}
      {showUpload && (
        <div className="card">
          <div style={{ fontSize: '11px', color: '#ef4444', marginBottom: '12px', padding: '8px 12px', background: '#1a0a0a', border: '1px solid #ef4444', borderRadius: '8px' }}>
            ⚠️ Upload only repurposed (initially spoofed) content. Do not upload original pictures.
          </div>

          {/* File picker */}
          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            style={{
              border: '2px dashed #333', borderRadius: '10px', padding: '20px',
              textAlign: 'center', cursor: uploading ? 'wait' : 'pointer', color: '#555',
              marginBottom: '16px'
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff4500'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; }}
          >
            <div style={{ fontSize: '28px', marginBottom: '6px' }}>📁</div>
            <div style={{ fontSize: '13px' }}>Drop files here or click to select</div>
            <div style={{ fontSize: '11px', marginTop: '4px' }}>JPG, PNG, GIF, MP4</div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,video/mp4"
            multiple
            style={{ display: 'none' }}
            onChange={handleFilesSelected}
            disabled={uploading}
          />

          {uploadError && (
            <div style={{ fontSize: '13px', color: '#ef4444', marginBottom: '12px' }}>
              ✗ {uploadError}
            </div>
          )}

          {/* Global upload settings */}
          {pendingFiles.length > 0 && (
            <div style={{ background: '#0f0f0f', borderRadius: '8px', padding: '14px', marginBottom: '16px', border: '1px solid #2a2a2a' }}>
              <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '600', marginBottom: '10px' }}>Upload Settings (applies to all files)</div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '10px' }}>
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <label style={{ fontSize: '10px', color: '#aaaaaa', display: 'block', marginBottom: '2px' }}>FOLDER</label>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <select
                      value={uploadFolder}
                      onChange={e => {
                        setUploadFolder(e.target.value);
                        setPendingFiles(prev => prev.map(f => ({ ...f, folder_id: e.target.value || null })));
                      }}
                      style={{ flex: 1, fontSize: '12px' }}
                    >
                      <option value="">📂 Unfiled</option>
                      {folders.map(f => (
                        <option key={f.id} value={f.id}>📁 {f.name}</option>
                      ))}
                    </select>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: '11px', padding: '4px 8px', whiteSpace: 'nowrap' }}
                      onClick={async () => {
                        const name = prompt('New folder name:');
                        if (name?.trim()) {
                          try {
                            const res = await axios.post(`${API}/api/vault/folders`, {
                              creator_id: creatorId,
                              name: name.trim(),
                              parent_id: activeFolder && activeFolder !== 'unfiled' ? activeFolder : null
                            });
                            await fetchFolders();
                            const newId = res.data.id;
                            setUploadFolder(newId);
                            setPendingFiles(prev => prev.map(f => ({ ...f, folder_id: newId })));
                          } catch (err) {
                            notifyError(err, 'Could not create folder');
                          }
                        }
                      }}
                    >+ New</button>
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '10px', color: '#aaaaaa', display: 'block', marginBottom: '2px' }}>COSTUME / CHARACTER NAME (applies to all)</label>
                <input
                  placeholder="e.g. Maid Cosplay, Elf Princess..."
                  value={uploadTitle}
                  onChange={e => {
                    setUploadTitle(e.target.value);
                    setPendingFiles(prev => prev.map(f => ({ ...f, title: e.target.value })));
                  }}
                  style={{ width: '100%', fontSize: '12px' }}
                />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '10px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>TAGS (applies to all)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {contentTags.map(tag => {
                    const active = uploadTags.includes(tag.name);
                    return (
                      <span
                        key={tag.id}
                        onClick={() => {
                          const newTags = active ? uploadTags.filter(t => t !== tag.name) : [...uploadTags, tag.name];
                          setUploadTags(newTags);
                          setPendingFiles(prev => prev.map(f => ({ ...f, tags: newTags })));
                        }}
                        style={{
                          padding: '3px 10px', borderRadius: '12px', fontSize: '11px', cursor: 'pointer',
                          background: active ? '#ff4500' : '#2a2a2a',
                          color: active ? '#fff' : '#aaaaaa',
                          border: active ? '1px solid #ff4500' : '1px solid #444'
                        }}
                      >{tag.name}</span>
                    );
                  })}
                  {contentTags.length === 0 && <span style={{ color: '#555', fontSize: '11px' }}>No tags — add some in Tag Library</span>}
                </div>
              </div>
              <div style={{ fontSize: '9px', color: '#555', marginTop: '6px' }}>Click individual thumbnails below to customize per file</div>
            </div>
          )}

          {/* Pending files grid */}
          {pendingFiles.length > 0 && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                {pendingFiles.map((pf, idx) => (
                  <div
                    key={idx}
                    onClick={() => setEditingFileIndex(editingFileIndex === idx ? null : idx)}
                    style={{
                      background: '#0f0f0f', borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
                      border: editingFileIndex === idx ? '2px solid #ff4500' : '1px solid #2a2a2a'
                    }}
                  >
                    <div style={{ position: 'relative', paddingTop: '100%', background: '#1a1a1a' }}>
                      <img
                        src={pf.preview}
                        alt={pf.file.name}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <button
                        onClick={(e) => { e.stopPropagation(); removePendingFile(idx); }}
                        style={{
                          position: 'absolute', top: '4px', right: '4px',
                          background: 'rgba(0,0,0,0.7)', color: '#ff4500', border: 'none',
                          borderRadius: '50%', width: '22px', height: '22px',
                          cursor: 'pointer', fontSize: '13px', fontWeight: '700',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >×</button>
                      {(pf.title || pf.tags.length > 0) && (
                        <div style={{
                          position: 'absolute', bottom: '4px', left: '4px',
                          background: 'rgba(255,69,0,0.8)', color: '#fff',
                          padding: '1px 6px', borderRadius: '6px', fontSize: '9px', fontWeight: '600'
                        }}>✓ edited</div>
                      )}
                    </div>
                    <div style={{ padding: '6px', fontSize: '10px', color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {pf.file.name}
                    </div>
                  </div>
                ))}
              </div>

              {/* Edit panel for selected file */}
              {editingFileIndex !== null && pendingFiles[editingFileIndex] && (
                <div style={{ background: '#0f0f0f', borderRadius: '8px', padding: '14px', marginBottom: '16px', border: '1px solid #ff4500' }}>
                  <div style={{ fontSize: '12px', color: '#ff4500', fontWeight: '600', marginBottom: '10px' }}>
                    Editing: {pendingFiles[editingFileIndex].file.name}
                  </div>
                  <select
                    value={pendingFiles[editingFileIndex].folder_id || ''}
                    onChange={e => updatePendingFile(editingFileIndex, 'folder_id', e.target.value || null)}
                    style={{ width: '100%', marginBottom: '8px', fontSize: '12px' }}
                  >
                    <option value="">📂 No folder (Unfiled)</option>
                    {folders.map(f => (
                      <option key={f.id} value={f.id}>📁 {f.name}</option>
                    ))}
                  </select>
                  <input
                    placeholder="Costume / Character name"
                    value={pendingFiles[editingFileIndex].title}
                    onChange={e => updatePendingFile(editingFileIndex, 'title', e.target.value)}
                    style={{ width: '100%', marginBottom: '8px' }}
                  />
                  <input
                    placeholder="Notes"
                    value={pendingFiles[editingFileIndex].notes}
                    onChange={e => updatePendingFile(editingFileIndex, 'notes', e.target.value)}
                    style={{ width: '100%', marginBottom: '10px' }}
                  />
                  <div style={{ fontSize: '11px', color: '#aaaaaa', marginBottom: '6px' }}>CONTENT TAGS</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {contentTags.map(tag => {
                      const active = pendingFiles[editingFileIndex].tags.includes(tag.name);
                      return (
                        <span
                          key={tag.id}
                          onClick={() => togglePendingFileTag(editingFileIndex, tag.name)}
                          style={{
                            padding: '3px 10px', borderRadius: '12px', fontSize: '11px', cursor: 'pointer',
                            background: active ? '#ff4500' : '#2a2a2a',
                            color: active ? '#fff' : '#aaaaaa',
                            border: active ? '1px solid #ff4500' : '1px solid #444'
                          }}
                        >{tag.name}</span>
                      );
                    })}
                    {contentTags.length === 0 && <span style={{ color: '#555', fontSize: '11px' }}>No tags yet — add some in Tag Library</span>}
                  </div>
                </div>
              )}

              {/* Upload button */}
              <button
                onClick={handleUploadAll}
                disabled={uploading}
                style={{ width: '100%', opacity: uploading ? 0.6 : 1, cursor: uploading ? 'wait' : 'pointer' }}
              >
                {uploading
                  ? '⏳ Uploading...'
                  : `📤 Upload ${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''}`}
              </button>
            </>
          )}
        </div>
      )}

      {/* Folder Cards — only shown for 'available' status */}
      {statusFilter === 'available' && (
      <div className="card">
        {activeFolder ? (
          // Breadcrumb when inside a folder
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: currentSubfolders.length > 0 ? '12px' : '4px', flexWrap: 'wrap' }}>
            <span
              onClick={() => { setActiveFolder(null); setLastVisitedFolder(null); setDisplayCount(batchSize); setSelectMode(false); setSelectedItems([]); setFolderSelectMode(false); setSelectedFolders([]); }}
              style={{ fontSize: '13px', color: '#f59e0b', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
            >📁 All Items</span>
            {breadcrumbPath.map((folder, i) => (
              <React.Fragment key={folder.id}>
                <span style={{ color: '#555' }}>›</span>
                {i < breadcrumbPath.length - 1 ? (
                  <span
                    onClick={() => { setActiveFolder(folder.id); setDisplayCount(batchSize); }}
                    style={{ fontSize: '13px', color: '#f59e0b', cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                  >📂 {folder.name}</span>
                ) : (
                  <span style={{ fontSize: '13px', color: '#fff', fontWeight: '600' }}>📂 {folder.name}</span>
                )}
              </React.Fragment>
            ))}
            {activeFolder === 'unfiled' && (
              <>
                <span style={{ color: '#555' }}>›</span>
                <span style={{ fontSize: '13px', color: '#fff', fontWeight: '600' }}>📂 Unfiled</span>
              </>
            )}
            <span style={{ fontSize: '11px', color: '#555', marginLeft: '4px' }}>
              ({filtered.length} items)
            </span>

            {currentSubfolders.length > 0 && (
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px', alignItems: 'center' }}>
                <select
                  value={folderSortBy}
                  onChange={e => setFolderSortBy(e.target.value)}
                  style={{ fontSize: '11px', padding: '3px 6px', background: '#1a1a1a', color: '#aaaaaa', border: '1px solid #333', borderRadius: '6px' }}
                >
                  <option value="name_asc">Name A→Z</option>
                  <option value="name_desc">Name Z→A</option>
                  <option value="date_new">Newest first</option>
                  <option value="date_old">Oldest first</option>
                  <option value="items_most">Most items</option>
                  <option value="items_least">Fewest items</option>
                </select>
                {['grid', 'list'].map(v => (
                  <button key={v} onClick={() => setFolderViewMode(v)} style={{
                    padding: '3px 8px', fontSize: '11px', borderRadius: '6px', border: 'none',
                    cursor: 'pointer', background: folderViewMode === v ? '#f59e0b' : '#2a2a2a',
                    color: folderViewMode === v ? '#fff' : '#aaaaaa'
                  }}>{v === 'grid' ? '⊞' : '≡'}</button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: folderSelectMode ? '8px' : '12px' }}>
            <span style={{ fontSize: '13px', color: '#aaaaaa' }}>📁 Folders</span>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <select
                value={folderSortBy}
                onChange={e => setFolderSortBy(e.target.value)}
                style={{ fontSize: '13px', padding: '7px 10px', background: '#1a1a1a', color: '#aaaaaa', border: '1px solid #333', borderRadius: '8px' }}
              >
                <option value="name_asc">Name A→Z</option>
                <option value="name_desc">Name Z→A</option>
                <option value="date_new">Newest first</option>
                <option value="date_old">Oldest first</option>
                <option value="items_most">Most items</option>
                <option value="items_least">Fewest items</option>
              </select>
              {['grid', 'list'].map(v => (
                <button key={v} onClick={() => setFolderViewMode(v)} style={{
                  padding: '7px 13px', fontSize: '14px', borderRadius: '8px', border: 'none',
                  cursor: 'pointer', background: folderViewMode === v ? '#f59e0b' : '#2a2a2a',
                  color: folderViewMode === v ? '#fff' : '#aaaaaa'
                }}>{v === 'grid' ? '⊞' : '≡'}</button>
              ))}
              <button onClick={() => { setFolderSelectMode(!folderSelectMode); setSelectedFolders([]); }} style={{
                padding: '8px 18px', fontSize: '14px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600',
                background: folderSelectMode ? '#2a2a2a' : '#f59e0b', color: '#fff'
              }}>{folderSelectMode ? 'Cancel' : 'Select'}</button>
            </div>
          </div>
          {folderSelectMode && (() => {
            const none = selectedFolders.length === 0;
            const btn = (bg) => ({ fontSize: '14px', fontWeight: '600', padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: none ? 'not-allowed' : 'pointer', background: bg, color: '#fff', opacity: none ? 0.35 : 1 });
            return (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', background: '#1a1400', border: '1px solid #92400e', borderRadius: '10px', padding: '12px 16px' }}>
              <span style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '700' }}>
                {none ? '👆 Tap folders to select them, then pick an action' : `${selectedFolders.length} folder${selectedFolders.length !== 1 ? 's' : ''} selected:`}
              </span>
              {selectedFolders.length === 1 && (
                <button style={btn('#2563eb')} onClick={renameSelectedFolder}>✏️ Rename</button>
              )}
              <button disabled={none} style={btn('#f59e0b')} onClick={() => describeFolders(false)}>🤖 Describe missing</button>
              <button disabled={none} style={btn('#7c3aed')} onClick={() => describeFolders(true)}>🔄 Re-describe all</button>
              <button disabled={none} style={btn('#444')} onClick={() => deleteFolders(false)}>🗑 Delete (keep items)</button>
              <button disabled={none} style={btn('#b91c1c')} onClick={() => deleteFolders(true)}>🗑 Delete + content</button>
              <button style={{ ...btn('#2a2a2a'), opacity: 1, cursor: 'pointer', marginLeft: 'auto' }} onClick={() => setSelectedFolders((activeFolder && activeFolder !== 'unfiled' ? currentSubfolders : folders.filter(f => !f.parent_id)).map(f => f.id))}>Select all</button>
            </div>
            );
          })()}
          </div>
        )}

        {(() => {
          const sortFolders = (list) => {
            return [...list].sort((a, b) => {
              switch (folderSortBy) {
                case 'name_asc': return a.name.localeCompare(b.name);
                case 'name_desc': return b.name.localeCompare(a.name);
                case 'date_new': return new Date(b.created_at) - new Date(a.created_at);
                case 'date_old': return new Date(a.created_at) - new Date(b.created_at);
                case 'items_most': return (b.item_count || 0) - (a.item_count || 0);
                case 'items_least': return (a.item_count || 0) - (b.item_count || 0);
                default: return 0;
              }
            });
          };

          const foldersToShow = sortFolders(
            activeFolder && activeFolder !== 'unfiled'
              ? currentSubfolders
              : activeFolder === null
                ? folders.filter(f => !f.parent_id)
                : []
          );

          const showUnfiled = !activeFolder;

          if (foldersToShow.length === 0 && !showUnfiled && !activeFolder) return null;
          if (foldersToShow.length === 0 && activeFolder) return null;

          return folderViewMode === 'grid' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' }}>
              {foldersToShow.map(folder => (
                <div key={folder.id} style={{ background: '#0f0f0f', borderRadius: '8px', padding: '12px', border: selectedFolders.includes(folder.id) ? '2px solid #22c55e' : lastVisitedFolder === folder.id ? '2px solid #f59e0b' : '1px solid #2a2a2a', cursor: 'pointer', position: 'relative', transition: 'border-color 0.15s' }}
                  onClick={() => handleFolderClick(folder)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = selectedFolders.includes(folder.id) ? '#22c55e' : lastVisitedFolder === folder.id ? '#f59e0b' : '#2a2a2a'; setFolderMenuOpen(null); }}
                >
                  <div onClick={e => { e.stopPropagation(); setFolderMenuOpen(folderMenuOpen === folder.id ? null : folder.id); }}
                    style={{ position: 'absolute', top: '8px', right: '8px', color: '#555', cursor: 'pointer', fontSize: '16px', padding: '0 4px', borderRadius: '4px' }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = '#555'; }}
                  >⋮</div>
                  {folderMenuOpen === folder.id && (
                    <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: '28px', right: '8px', zIndex: 10, background: '#1a1a1a', border: '1px solid #444', borderRadius: '8px', padding: '4px', minWidth: '120px', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                      <div onClick={() => { setRenamingFolder(folder.id); setRenamingFolderValue(folder.name); setFolderMenuOpen(null); }} style={{ padding: '6px 10px', fontSize: '12px', color: '#fff', cursor: 'pointer', borderRadius: '4px' }} onMouseEnter={e => { e.currentTarget.style.background = '#2a2a2a'; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>✏️ Rename</div>
                      <div onClick={() => { const name = prompt('New subfolder name:'); if (name?.trim()) { axios.post(`${API}/api/vault/folders`, { creator_id: creatorId, name: name.trim(), parent_id: folder.id }).then(() => fetchFolders()).catch(err => notifyError(err, 'Could not create folder')); } setFolderMenuOpen(null); }} style={{ padding: '6px 10px', fontSize: '12px', color: '#fff', cursor: 'pointer', borderRadius: '4px' }} onMouseEnter={e => { e.currentTarget.style.background = '#2a2a2a'; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>📁 Add subfolder</div>
                      <div onClick={async () => { if (!window.confirm(`Delete folder "${folder.name}"? Items will become unfiled.`)) return; try { await axios.delete(`${API}/api/vault/folders/${folder.id}`); fetchFolders(); fetchItems(activeFolder); } catch (err) { notifyError(err, 'Could not delete folder'); } setFolderMenuOpen(null); }} style={{ padding: '6px 10px', fontSize: '12px', color: '#ef4444', cursor: 'pointer', borderRadius: '4px' }} onMouseEnter={e => { e.currentTarget.style.background = '#2a0a0a'; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>🗑 Delete (keep items)</div>
                      <div onClick={async () => { const hasSubfolders = folders.some(f => f.parent_id === folder.id); if (hasSubfolders) { alert('Delete subfolders first.'); setFolderMenuOpen(null); return; } if (!window.confirm(`Delete folder "${folder.name}" AND all ${folder.item_count} items inside? This cannot be undone.`)) { setFolderMenuOpen(null); return; } showActivity(`Deleting folder and ${folder.item_count} items...`); try { await axios.delete(`${API}/api/vault/folders/${folder.id}/with-content`); fetchFolders(); fetchItems(activeFolder); } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); } hideActivity(); setFolderMenuOpen(null); }} style={{ padding: '6px 10px', fontSize: '12px', color: '#ff4500', cursor: 'pointer', borderRadius: '4px' }} onMouseEnter={e => { e.currentTarget.style.background = '#2a0a0a'; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>🗑 Delete with content</div>
                    </div>
                  )}
                  {renamingFolder === folder.id ? (
                    <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: '4px' }}>
                      <input value={renamingFolderValue} onChange={e => setRenamingFolderValue(e.target.value)} autoFocus onKeyDown={async e => { if (e.key === 'Enter' && renamingFolderValue.trim()) { await axios.put(`${API}/api/vault/folders/${folder.id}`, { name: renamingFolderValue.trim() }); setRenamingFolder(null); fetchFolders(); } if (e.key === 'Escape') setRenamingFolder(null); }} style={{ flex: 1, fontSize: '12px', padding: '3px 6px' }} />
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: '22px', marginBottom: '4px' }}>📁</div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folder.name}</div>
                      <div style={{ fontSize: '10px', color: '#555', marginTop: '2px' }}>{folder.item_count} item{folder.item_count !== 1 ? 's' : ''}</div>
                    </>
                  )}
                </div>
              ))}
              {showUnfiled && (
                <div style={{ background: '#0f0f0f', borderRadius: '8px', padding: '12px', border: lastVisitedFolder === 'unfiled' ? '2px solid #f59e0b' : '1px solid #2a2a2a', cursor: 'pointer', transition: 'border-color 0.15s' }} onClick={() => { setActiveFolder('unfiled'); setDisplayCount(batchSize); setSelectMode(false); setSelectedItems([]); }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#555'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = lastVisitedFolder === 'unfiled' ? '#f59e0b' : '#2a2a2a'; }}>
                  <div style={{ fontSize: '22px', marginBottom: '4px' }}>📂</div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#aaaaaa' }}>Unfiled</div>
                  <div style={{ fontSize: '10px', color: '#555', marginTop: '2px' }}>{unfiledCount} item{unfiledCount !== 1 ? 's' : ''}</div>
                </div>
              )}
              <div style={{ background: '#0a0a0a', borderRadius: '8px', padding: '12px', border: '1px dashed #333', cursor: 'pointer', transition: 'border-color 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }} onClick={() => { const name = prompt('New folder name:'); if (name?.trim()) { const parentId = activeFolder && activeFolder !== 'unfiled' ? activeFolder : null; axios.post(`${API}/api/vault/folders`, { creator_id: creatorId, name: name.trim(), parent_id: parentId }).then(() => fetchFolders()).catch(err => notifyError(err, 'Could not create folder')); } }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; }}>
                <div style={{ fontSize: '20px', color: '#555' }}>+</div>
                <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>New Folder</div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {foldersToShow.map(folder => (
                <div key={folder.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#0f0f0f', borderRadius: '6px', border: selectedFolders.includes(folder.id) ? '2px solid #22c55e' : lastVisitedFolder === folder.id ? '2px solid #f59e0b' : '1px solid #2a2a2a', cursor: 'pointer', transition: 'border-color 0.15s', position: 'relative' }} onClick={() => handleFolderClick(folder)} onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = selectedFolders.includes(folder.id) ? '#22c55e' : lastVisitedFolder === folder.id ? '#f59e0b' : '#2a2a2a'; setFolderMenuOpen(null); }}>
                  <span style={{ fontSize: '16px' }}>📁</span>
                  <span style={{ fontSize: '13px', color: '#fff', fontWeight: '600', flex: 1 }}>{folder.name}</span>
                  <span style={{ fontSize: '11px', color: '#555' }}>{folder.item_count} items</span>
                  <div onClick={e => { e.stopPropagation(); setFolderMenuOpen(folderMenuOpen === folder.id ? null : folder.id); }} style={{ color: '#555', cursor: 'pointer', fontSize: '16px', padding: '0 4px' }} onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }} onMouseLeave={e => { e.currentTarget.style.color = '#555'; }}>⋮</div>
                  {folderMenuOpen === folder.id && (
                    <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: '36px', right: '8px', zIndex: 10, background: '#1a1a1a', border: '1px solid #444', borderRadius: '8px', padding: '4px', minWidth: '160px', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                      <div onClick={() => { setRenamingFolder(folder.id); setRenamingFolderValue(folder.name); setFolderMenuOpen(null); }} style={{ padding: '6px 10px', fontSize: '12px', color: '#fff', cursor: 'pointer', borderRadius: '4px' }} onMouseEnter={e => { e.currentTarget.style.background = '#2a2a2a'; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>✏️ Rename</div>
                      <div onClick={() => { const name = prompt('New subfolder name:'); if (name?.trim()) { axios.post(`${API}/api/vault/folders`, { creator_id: creatorId, name: name.trim(), parent_id: folder.id }).then(() => fetchFolders()).catch(err => notifyError(err, 'Could not create folder')); } setFolderMenuOpen(null); }} style={{ padding: '6px 10px', fontSize: '12px', color: '#fff', cursor: 'pointer', borderRadius: '4px' }} onMouseEnter={e => { e.currentTarget.style.background = '#2a2a2a'; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>📁 Add subfolder</div>
                      <div onClick={async () => { if (!window.confirm(`Delete "${folder.name}"? Items become unfiled.`)) return; try { await axios.delete(`${API}/api/vault/folders/${folder.id}`); fetchFolders(); fetchItems(activeFolder); } catch (err) { notifyError(err, 'Could not delete folder'); } setFolderMenuOpen(null); }} style={{ padding: '6px 10px', fontSize: '12px', color: '#ef4444', cursor: 'pointer', borderRadius: '4px' }} onMouseEnter={e => { e.currentTarget.style.background = '#2a0a0a'; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>🗑 Delete (keep items)</div>
                      <div onClick={async () => { const hasSubfolders = folders.some(f => f.parent_id === folder.id); if (hasSubfolders) { alert('Delete subfolders first.'); setFolderMenuOpen(null); return; } if (!window.confirm(`Delete folder "${folder.name}" AND all ${folder.item_count} items inside? This cannot be undone.`)) { setFolderMenuOpen(null); return; } showActivity(`Deleting folder and ${folder.item_count} items...`); try { await axios.delete(`${API}/api/vault/folders/${folder.id}/with-content`); fetchFolders(); fetchItems(activeFolder); } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); } hideActivity(); setFolderMenuOpen(null); }} style={{ padding: '6px 10px', fontSize: '12px', color: '#ff4500', cursor: 'pointer', borderRadius: '4px' }} onMouseEnter={e => { e.currentTarget.style.background = '#2a0a0a'; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>🗑 Delete with content</div>
                    </div>
                  )}
                  {renamingFolder === folder.id && (
                    <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', left: '40px', right: '40px', top: '6px' }}>
                      <input value={renamingFolderValue} onChange={e => setRenamingFolderValue(e.target.value)} autoFocus onKeyDown={async e => { if (e.key === 'Enter' && renamingFolderValue.trim()) { await axios.put(`${API}/api/vault/folders/${folder.id}`, { name: renamingFolderValue.trim() }); setRenamingFolder(null); fetchFolders(); } if (e.key === 'Escape') setRenamingFolder(null); }} style={{ width: '100%', fontSize: '12px', padding: '4px 8px' }} />
                    </div>
                  )}
                </div>
              ))}
              {showUnfiled && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#0f0f0f', borderRadius: '6px', border: lastVisitedFolder === 'unfiled' ? '2px solid #f59e0b' : '1px solid #2a2a2a', cursor: 'pointer' }} onClick={() => { setActiveFolder('unfiled'); setDisplayCount(batchSize); setSelectMode(false); setSelectedItems([]); }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#555'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = lastVisitedFolder === 'unfiled' ? '#f59e0b' : '#2a2a2a'; }}>
                  <span style={{ fontSize: '16px' }}>📂</span>
                  <span style={{ fontSize: '13px', color: '#aaaaaa', flex: 1 }}>Unfiled</span>
                  <span style={{ fontSize: '11px', color: '#555' }}>{unfiledCount} items</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#0a0a0a', borderRadius: '6px', border: '1px dashed #333', cursor: 'pointer' }} onClick={() => { const name = prompt('New folder name:'); if (name?.trim()) { const parentId = activeFolder && activeFolder !== 'unfiled' ? activeFolder : null; axios.post(`${API}/api/vault/folders`, { creator_id: creatorId, name: name.trim(), parent_id: parentId }).then(() => fetchFolders()).catch(err => notifyError(err, 'Could not create folder')); } }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; }}>
                <span style={{ fontSize: '16px', color: '#555' }}>+</span>
                <span style={{ fontSize: '13px', color: '#555' }}>New Folder</span>
              </div>
            </div>
          );
        })()}
      </div>
      )}

      {/* Filters & Controls */}
      <div className="card">
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Status filter */}
          {['available', 'drafted', 'rejected'].map(s => (
            <span
              key={s}
              onClick={() => { setStatusFilter(s); setDisplayCount(batchSize); setSelectMode(false); setSelectedItems([]); if (s === 'available') setActiveFolder(null); }}
              style={{
                padding: '5px 14px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer',
                background: statusFilter === s ? getStatusColor(s) : '#2a2a2a',
                color: statusFilter === s ? '#ffffff' : '#aaaaaa', fontWeight: '600'
              }}
            >{s}</span>
          ))}

          <div style={{ width: '1px', height: '20px', background: '#333', margin: '0 4px' }} />

          {/* Media type filters */}
          {[
            { key: 'all', label: 'All' },
            { key: 'images', label: '🖼 Images' },
            { key: 'gifs', label: '🎞 GIFs' },
            { key: 'videos', label: '🎬 Videos' }
          ].map(m => (
            <span
              key={m.key}
              onClick={() => { setMediaFilter(m.key); setDisplayCount(batchSize); }}
              style={{
                padding: '5px 14px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer',
                background: mediaFilter === m.key ? '#3b82f6' : '#2a2a2a',
                color: mediaFilter === m.key ? '#ffffff' : '#aaaaaa', fontWeight: '600'
              }}
            >{m.label}</span>
          ))}

          <div style={{ width: '1px', height: '20px', background: '#333', margin: '0 4px' }} />

          {/* Tag filters */}
          {contentTags.map(tag => {
            const active = tagFilter.includes(tag.name);
            return (
              <span
                key={tag.id}
                onClick={() => toggleTagFilter(tag.name)}
                style={{
                  padding: '5px 14px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer',
                  background: active ? '#ff4500' : '#2a2a2a',
                  color: active ? '#ffffff' : '#aaaaaa',
                  border: active ? '1px solid #ff4500' : '1px solid #444'
                }}
              >{tag.name}</span>
            );
          })}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              placeholder="Search..."
              value={search}
              onChange={e => { setSearch(e.target.value); setDisplayCount(batchSize); }}
              style={{ width: '160px', fontSize: '12px', padding: '5px 10px' }}
            />
            <select value={batchSize === Infinity ? "all" : batchSize} onChange={e => { const v = e.target.value === "all" ? Infinity : parseInt(e.target.value); setBatchSize(v); setDisplayCount(v); }} style={{ fontSize: "12px", padding: "5px 8px", background: "#1a1a1a", color: "#aaaaaa", border: "1px solid #333", borderRadius: "6px" }}><option value={25}>25 / view</option><option value={50}>50 / view</option><option value={100}>100 / view</option><option value="all">All</option></select>
            
            <select
              value={sortBy}
              onChange={e => { setSortBy(e.target.value); setDisplayCount(batchSize); }}
              style={{ fontSize: '12px', padding: '5px 8px', background: '#1a1a1a', color: '#aaaaaa', border: '1px solid #333', borderRadius: '6px' }}
            >
              <option value="date_new">Newest upload</option>
              <option value="date_old">Oldest upload</option>
              <option value="modified_new">Recently modified</option>
              <option value="modified_old">Least recently modified</option>
              <option value="name_asc">Name A→Z</option>
              <option value="name_desc">Name Z→A</option>
            </select>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['grid', 'list'].map(v => (
                <button
                  key={v}
                  onClick={() => setViewMode(v)}
                  style={{
                    padding: '5px 10px', fontSize: '12px', borderRadius: '6px', border: 'none',
                    cursor: 'pointer', fontWeight: '600',
                    background: viewMode === v ? '#ff4500' : '#2a2a2a',
                    color: viewMode === v ? '#fff' : '#aaaaaa'
                  }}
                >{v === 'grid' ? '⊞' : '≡'}</button>
              ))}
            </div>
            {filtered.length > 0 && (
              <button
                style={{
                  fontSize: '13px', padding: '5px 14px', fontWeight: '600',
                  background: selectMode ? '#2a2a2a' : '#ff4500',
                  color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer'
                }}
                onClick={() => { setSelectMode(!selectMode); setSelectedItems([]); }}
              >{selectMode ? 'Cancel' : 'Select'}</button>
            )}
          </div>
        </div>

        {selectMode && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }}
              onClick={() => setSelectedItems(selectedItems.length === filtered.length ? [] : filtered.map(i => i.id))}>
              {selectedItems.length === filtered.length ? 'Deselect All' : `Select All (${filtered.length})`}
            </button>
            {selectedItems.length > 0 && (
              <>
                <button style={{ fontSize: '12px', padding: '4px 12px' }} onClick={() => setBulkEditOpen(true)}>
                  ✏️ Bulk Edit ({selectedItems.length})
                </button>
                <button className="btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }} onClick={() => setShowMoveModal(true)}>
                  📁 Move to... ({selectedItems.length})
                </button>

                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#0f0f0f', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '2px 4px' }}>
                  <select value={bulkDescPresetId} onChange={e => setBulkDescPresetId(e.target.value)} title="Description prompt"
                    style={{ fontSize: '11px', padding: '4px 6px', background: '#1a1a1a', color: '#aaa', border: '1px solid #333', borderRadius: '6px', maxWidth: '130px' }}>
                    <option value="">Default prompt</option>
                    {descPresets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                <button className="btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }} onClick={async () => {
                  const preset = descPresets.find(p => p.id === bulkDescPresetId);
                  const promptLabel = preset ? `"${preset.name}"` : 'the Default prompt';
                  if (!window.confirm(`Generate AI descriptions for ${selectedItems.length} selected items using ${promptLabel}?\n\nItems that already have a description are skipped.`)) return;
                  const targetIds = [...selectedItems];
                  let res;
                  try {
                    res = await axios.post(`${API}/api/vault/bulk-describe`, {
                      vault_item_ids: targetIds,
                      custom_prompt: preset?.custom_prompt || ''
                    });
                  } catch (err) {
                    alert('Describe failed: ' + (err.response?.data?.error || err.message));
                    return;
                  }
                  const { queued = 0, queued_thumbnail_first = 0, skipped_described = 0, skipped_no_thumbnail = 0 } = res.data;
                  const target = queued + queued_thumbnail_first;
                  if (target === 0) {
                    alert(`🤖 Nothing to describe.${skipped_described ? ` ${skipped_described} already described.` : ''}${skipped_no_thumbnail ? ` ${skipped_no_thumbnail} skipped.` : ''}`);
                    return;
                  }
                  // Exit select mode and show the processing popup while the worker runs
                  setSelectMode(false);
                  setSelectedItems([]);
                  showActivity(`Describing ${target} image${target !== 1 ? 's' : ''}…`);

                  // Poll the folder until the queued items have descriptions, then
                  // refresh the folder view in place (stays in the same folder)
                  const targetSet = new Set(targetIds);
                  const folder = activeFolder;
                  const startedFor = creatorId;
                  const started = Date.now();
                  const poll = setInterval(async () => {
                    try {
                      let url = `${API}/api/vault/creator/${creatorId}?`;
                      if (folder === 'unfiled') url += 'folder_id=unfiled&';
                      else if (folder) url += `folder_id=${folder}&`;
                      url = url.replace(/[&?]$/, '');
                      const r = await axios.get(url);
                      const doneCount = r.data.filter(i => targetSet.has(i.id) && i.image_description).length;
                      showActivity(`Describing… ${doneCount}/${target} done`);
                      if (doneCount >= target || Date.now() - started > 180000) {
                        stopPoller(poll, timeoutId);
                        hideActivity();
                        // refresh the folder with new descriptions — only if it's still the one on screen
                        if (viewRef.current.creatorId === startedFor && viewRef.current.activeFolder === folder) {
                          itemsSeqRef.current++;
                          setItems(r.data);
                        }
                      }
                    } catch (e) { /* keep polling */ }
                  }, 3000);
                  const timeoutId = setTimeout(() => { stopPoller(poll, timeoutId); hideActivity(); }, 185000);
                  activePollersRef.current.push({ interval: poll, timeout: timeoutId });
                }}>
                  🤖 Describe ({selectedItems.length})
                </button>
                </div>

                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#0f0f0f', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '2px 4px' }}>
                  <button className="btn-secondary" title="Darken selected images" style={{ fontSize: '13px', padding: '4px 10px' }} onClick={() => applyBrightness(-1)}>🌙 −</button>
                  <select value={brightnessPct} onChange={e => setBrightnessPct(parseInt(e.target.value))}
                    style={{ fontSize: '12px', padding: '4px 6px', background: '#1a1a1a', color: '#aaa', border: '1px solid #333', borderRadius: '6px' }}>
                    {[5, 10, 15, 20, 25, 30].map(p => <option key={p} value={p}>{p}%</option>)}
                  </select>
                  <button className="btn-secondary" title="Brighten selected images" style={{ fontSize: '13px', padding: '4px 10px' }} onClick={() => applyBrightness(1)}>☀️ +</button>
                </div>
                <button className="btn-danger" style={{ fontSize: '12px', padding: '4px 12px' }} onClick={bulkDelete}>
                  Delete ({selectedItems.length})
                </button>
              </>
            )}
            <span style={{ fontSize: '12px', color: '#555' }}>{selectedItems.length} selected</span>
          </div>
        )}
      </div>

      {/* Items */}
      <div className="card">
        {loading ? (
          <p className="empty">Loading...</p>
        ) : displayed.length === 0 ? (
          <p className="empty">No {statusFilter} content{tagFilter.length > 0 ? ' with selected tags' : ''}.</p>
        ) : viewMode === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px' }}>
            {displayed.map(item => {
              const isSelected = selectedItems.includes(item.id);
              return (
                <div
                  key={item.id}
                  onClick={() => {
                    if (selectMode) { toggleSelectItem(item.id); return; }
                    setDetailItem(item);
                    setEditingTitle(item.title_suggestion || '');
                    setEditingNotes(item.notes || '');
                    setShowUsageHistory(false);
                    setDetailHistory([]);
                  }}
                  style={{
                    background: '#0f0f0f', borderRadius: '8px',
                    border: isSelected ? '2px solid #ff4500' : '1px solid #2a2a2a',
                    overflow: 'hidden', cursor: 'pointer', transition: 'border-color 0.15s'
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = '#555'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = '#2a2a2a'; }}
                >
                  <div style={{ position: 'relative', paddingTop: '75%', background: '#1a1a1a' }}>
                    {(item.thumbnail_url || item.file_url) ? (
                      <>
                        <img
                        src={item.thumbnail_url || (item.media_type === 'gif' || item.media_type === 'video' ? '' : item.file_url)}
                        
                          alt={item.original_filename}
                          loading="lazy"
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        {!item.thumbnail_url && (item.media_type === 'gif' || item.media_type === 'video') && (
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#555', fontSize: '11px', textAlign: 'center' }}>
                          ⏳<br/>Processing
                        </div>
                      )}
                        {item.media_type === 'gif' && (
                          <span style={{ position: 'absolute', top: '6px', left: '6px', background: 'rgba(59,130,246,0.9)', color: '#fff', padding: '1px 6px', borderRadius: '6px', fontSize: '9px', fontWeight: '700' }}>GIF</span>
                        )}
                        {item.media_type === 'video' && (
                          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ color: '#fff', fontSize: '16px', marginLeft: '2px' }}>▶</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#444', fontSize: '12px' }}>
                        No preview
                      </div>
                    )}
                    <span style={{
                      position: 'absolute', top: '6px', right: '6px',
                      background: getStatusColor(item.status), color: '#fff',
                      padding: '2px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '700'
                    }}>{item.status === 'drafted' ? '📌 drafted' : item.status}</span>
                    {!item.file_url && (
                      <span style={{
                        position: 'absolute', top: '6px', left: '6px',
                        background: '#ef4444', color: '#fff',
                        padding: '2px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '700'
                      }}>⚠ No file</span>
                    )}
                    {item.spoof_cycle > 0 && (
                      <span style={{
                        position: 'absolute', bottom: '6px', right: '6px',
                        background: 'rgba(0,0,0,0.8)', color: '#f59e0b',
                        padding: '2px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '700'
                      }}>cycle {item.spoof_cycle}</span>
                    )}
                    {selectMode && (
                      <div style={{
                        position: 'absolute', top: '6px', left: '6px',
                        width: '20px', height: '20px', borderRadius: '50%',
                        background: isSelected ? '#ff4500' : 'rgba(0,0,0,0.6)',
                        border: '2px solid ' + (isSelected ? '#ff4500' : '#aaaaaa'),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '11px', color: '#fff', fontWeight: '700'
                      }}>{isSelected ? '✓' : ''}</div>
                    )}
                    {(item.content_tags || []).length > 0 && (
                      <div style={{ position: 'absolute', bottom: '6px', left: '6px', display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                        {(item.content_tags || []).map(tag => (
                          <span key={tag} style={{ background: 'rgba(255,69,0,0.8)', color: '#fff', fontSize: '8px', padding: '1px 5px', borderRadius: '6px' }}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '8px' }}>
                    {item.title_suggestion && (
                      <div style={{ fontSize: '11px', color: '#fff', fontWeight: '600', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title_suggestion}
                      </div>
                    )}
                    <div style={{ fontSize: '10px', color: '#555' }}>
                      {new Date(item.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {displayed.map(item => {
              const isSelected = selectedItems.includes(item.id);
              return (
                <div
                  key={item.id}
                  onClick={() => {
                    if (selectMode) { toggleSelectItem(item.id); return; }
                    setDetailItem(item);
                    setEditingTitle(item.title_suggestion || '');
                    setEditingNotes(item.notes || '');
                    setShowUsageHistory(false);
                    setDetailHistory([]);
                  }}
                  style={{
                    display: 'flex', gap: '12px', alignItems: 'center',
                    background: '#0f0f0f', borderRadius: '8px', padding: '10px 12px',
                    border: isSelected ? '2px solid #ff4500' : '1px solid #2a2a2a',
                    cursor: 'pointer'
                  }}
                >
                  {selectMode && (
                    <div style={{
                      width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                      background: isSelected ? '#ff4500' : 'transparent',
                      border: '2px solid ' + (isSelected ? '#ff4500' : '#555'),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '10px', color: '#fff'
                    }}>{isSelected ? '✓' : ''}</div>
                  )}
                  {(item.thumbnail_url || item.file_url) && (
                    <img src={item.thumbnail_url || item.file_url} alt="" loading="lazy" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', color: '#fff', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.title_suggestion || item.original_filename}
                    </div>
                    <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>
                      {item.original_filename} · {new Date(item.created_at).toLocaleDateString()}
                      {item.spoof_cycle > 0 && <span style={{ color: '#f59e0b', marginLeft: '8px' }}>cycle {item.spoof_cycle}</span>}
                    </div>
                    {(item.content_tags || []).length > 0 && (
                      <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                        {(item.content_tags || []).map(tag => (
                          <span key={tag} style={{ background: '#2a1000', color: '#ff4500', fontSize: '10px', padding: '1px 6px', borderRadius: '6px', border: '1px solid #ff4500' }}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span style={{
                    background: getStatusColor(item.status), color: '#fff',
                    padding: '2px 8px', borderRadius: '8px', fontSize: '10px', fontWeight: '700', flexShrink: 0
                  }}>{item.status}</span>
                </div>
              );
            })}
          </div>
        )}
        {hasMore && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px', gap: '12px', alignItems: 'center' }}>
            <button className="btn-secondary" style={{ fontSize: '12px', padding: '6px 20px' }} onClick={() => setDisplayCount(prev => batchSize === Infinity ? Infinity : prev + batchSize)}>Show more</button>
            <span style={{ color: '#555', fontSize: '11px' }}>{displayed.length} of {filtered.length}</span>
          </div>
        )}
        {!hasMore && filtered.length > 50 && (
          <div style={{ textAlign: 'center', padding: '12px', color: '#555', fontSize: '11px' }}>
            Showing all {filtered.length} items
          </div>
        )}
      </div>

      {/* Bulk Edit Modal */}
      {bulkEditOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 1001,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setBulkEditOpen(false)}>
          <div style={{
            background: '#1a1a1a', borderRadius: '12px', padding: '24px',
            width: '90%', maxWidth: '500px', maxHeight: '80vh', overflow: 'auto'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 4px 0' }}>Bulk Edit</h2>
            <p style={{ fontSize: '12px', color: '#555', marginBottom: '16px' }}>
              Editing {selectedItems.length} items. Leave fields empty to keep current values.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>COSTUME / CHARACTER NAME</label>
                <input
                  placeholder="Set name for all selected (leave empty to skip)"
                  value={bulkTitle}
                  onChange={e => setBulkTitle(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>NOTES</label>
                <input
                  placeholder="Set notes for all selected (leave empty to skip)"
                  value={bulkNotes}
                  onChange={e => setBulkNotes(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '11px', color: '#22c55e', display: 'block', marginBottom: '6px' }}>+ ADD TAGS</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {contentTags.map(tag => {
                    const active = bulkTagsAdd.includes(tag.name);
                    return (
                      <span
                        key={tag.id}
                        onClick={() => {
                          setBulkTagsAdd(prev => active ? prev.filter(t => t !== tag.name) : [...prev, tag.name]);
                          setBulkTagsRemove(prev => prev.filter(t => t !== tag.name));
                        }}
                        style={{
                          padding: '3px 10px', borderRadius: '12px', fontSize: '11px', cursor: 'pointer',
                          background: active ? '#22c55e' : '#2a2a2a',
                          color: active ? '#fff' : '#aaaaaa',
                          border: active ? '1px solid #22c55e' : '1px solid #444'
                        }}
                      >{tag.name}</span>
                    );
                  })}
                </div>
              </div>

              <div>
                <label style={{ fontSize: '11px', color: '#ef4444', display: 'block', marginBottom: '6px' }}>− REMOVE TAGS</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {contentTags.map(tag => {
                    const active = bulkTagsRemove.includes(tag.name);
                    return (
                      <span
                        key={tag.id}
                        onClick={() => {
                          setBulkTagsRemove(prev => active ? prev.filter(t => t !== tag.name) : [...prev, tag.name]);
                          setBulkTagsAdd(prev => prev.filter(t => t !== tag.name));
                        }}
                        style={{
                          padding: '3px 10px', borderRadius: '12px', fontSize: '11px', cursor: 'pointer',
                          background: active ? '#ef4444' : '#2a2a2a',
                          color: active ? '#fff' : '#aaaaaa',
                          border: active ? '1px solid #ef4444' : '1px solid #444'
                        }}
                      >{tag.name}</span>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setBulkEditOpen(false)}>Cancel</button>
              <button onClick={bulkEdit}>Apply to {selectedItems.length} items</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailItem && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 1001,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setDetailItem(null)}>
          {/* Navigation arrows — outside the modal */}
          <button
            onClick={e => { e.stopPropagation(); navigateDetail('prev'); }}
            style={{
              position: 'fixed', left: '16px', top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.1)', border: '1px solid #555',
              color: '#fff', borderRadius: '50%', width: '44px', height: '44px',
              cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1002
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          >←</button>
          <button
            onClick={e => { e.stopPropagation(); navigateDetail('next'); }}
            style={{
              position: 'fixed', right: '16px', top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.1)', border: '1px solid #555',
              color: '#fff', borderRadius: '50%', width: '44px', height: '44px',
              cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1002
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          >→</button>
          <div style={{
            position: 'fixed', bottom: '16px', left: '50%', transform: 'translateX(-50%)',
            fontSize: '11px', color: '#555', zIndex: 1002
          }}>
            {filtered.findIndex(i => i.id === detailItem.id) + 1} of {filtered.length}
          </div>
          <div style={{
            background: '#1a1a1a', borderRadius: '12px', padding: '24px',
            width: '94%', maxWidth: '980px', maxHeight: '92vh', overflow: 'auto'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
              <div style={{ flexShrink: 0 }}>
                {(detailItem.thumbnail_url || detailItem.file_url) && (
                  detailItem.media_type === 'video' ? (
                    <div style={{ width: '280px', height: '380px', background: '#000', borderRadius: '8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <video
                        key={detailItem.id}
                        src={detailItem.file_url}
                        controls
                        controlsList="nodownload noplaybackrate"
                        disablePictureInPicture
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </div>
                  ) : detailItem.media_type === 'gif' ? (
                    <img
                      key={detailItem.id}
                      src={detailItem.file_url}
                      alt=""
                      onClick={() => setDetailZoom(true)}
                      title="Click to enlarge"
                      style={{ width: '340px', maxHeight: '340px', objectFit: 'contain', borderRadius: '8px', background: '#0f0f0f', cursor: 'zoom-in' }}
                    />
                  ) : (
                    <img
                      key={detailItem.id}
                      src={(detailItem.file_url || detailItem.thumbnail_url) + (imgBust ? `?v=${imgBust}` : '')}
                      onError={(e) => { if (detailItem.thumbnail_url && e.target.src.indexOf(detailItem.thumbnail_url) === -1) e.target.src = detailItem.thumbnail_url; }}
                      alt=""
                      onClick={() => setDetailZoom(true)}
                      title="Click to enlarge"
                      style={{ width: '340px', maxHeight: '340px', objectFit: 'contain', borderRadius: '8px', background: '#0f0f0f', cursor: 'zoom-in' }}
                    />
                  )
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: '11px', color: '#555', marginBottom: '4px' }}>{detailItem.original_filename}</div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <span style={{ background: getStatusColor(detailItem.status), color: '#fff', padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '700' }}>
                    {detailItem.status}
                  </span>
                  <span style={{ background: '#2a2a2a', color: '#f59e0b', padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '700' }}>
                    Spoof cycle: {detailItem.spoof_cycle || 0}
                  </span>
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '10px', color: '#aaaaaa', display: 'block', marginBottom: '2px' }}>FOLDER</label>
                  <select
                    value={detailItem.folder_id || ''}
                    onChange={async e => {
                      const folderId = e.target.value || null;
                      try {
                        await axios.put(`${API}/api/vault/${detailItem.id}`, { folder_id: folderId });
                        setDetailItem(prev => ({ ...prev, folder_id: folderId }));
                        setItems(prev => prev.map(i => i.id === detailItem.id ? { ...i, folder_id: folderId } : i));
                        fetchFolders();
                      } catch (err) {
                        notifyError(err, 'Could not move item');
                      }
                    }}
                    style={{ width: '100%', fontSize: '12px' }}
                  >
                    <option value="">Unfiled</option>
                    {folders.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>

                <input
                  placeholder="Costume / Character name"
                  value={editingTitle}
                  onChange={e => setEditingTitle(e.target.value)}
                  style={{ width: '100%', marginBottom: '8px' }}
                />
                <input
                  placeholder="Notes"
                  value={editingNotes}
                  onChange={e => setEditingNotes(e.target.value)}
                  style={{ width: '100%', marginBottom: '12px' }}
                />

                <div style={{ marginBottom: '12px', background: '#0a0a0a', borderRadius: '8px', padding: '12px', border: '1px solid #2a2a2a' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '600' }}>👁️ AI IMAGE DESCRIPTION</label>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <span
                        onClick={() => setSelectedDescPresetId('')}
                        style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '9px', cursor: 'pointer', background: !selectedDescPresetId ? '#f59e0b' : '#2a2a2a', color: !selectedDescPresetId ? '#fff' : '#aaaaaa', border: `1px solid ${!selectedDescPresetId ? '#f59e0b' : '#444'}` }}
                      >Default</span>
                      {descPresets.map(p => (
                        <span
                          key={p.id}
                          onClick={() => setSelectedDescPresetId(selectedDescPresetId === p.id ? '' : p.id)}
                          style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '9px', cursor: 'pointer', background: selectedDescPresetId === p.id ? '#f59e0b' : '#2a2a2a', color: selectedDescPresetId === p.id ? '#fff' : '#aaaaaa', border: `1px solid ${selectedDescPresetId === p.id ? '#f59e0b' : '#444'}` }}
                        >{p.name}</span>
                      ))}
                    </div>
                  </div>
                  {detailItem.image_description ? (
                    <div>
                      <textarea
                        value={detailItem.image_description}
                        onChange={e => setDetailItem(prev => ({ ...prev, image_description: e.target.value }))}
                        style={{ width: '100%', fontSize: '13px', color: '#ddd', background: '#111', borderRadius: '6px', padding: '10px 12px', lineHeight: '1.6', marginBottom: '6px', border: '1px solid #333', resize: 'vertical', minHeight: '260px', fontFamily: 'inherit' }}
                      />
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          className="btn-secondary"
                          style={{ fontSize: '10px', padding: '3px 8px' }}
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/api/vault/${detailItem.id}`, { image_description: detailItem.image_description });
                              setItems(prev => prev.map(v => v.id === detailItem.id ? { ...v, image_description: detailItem.image_description } : v));
                            } catch (err) { notifyError(err, 'Save failed'); }
                          }}
                        >💾 Save</button>
                        <button
                          className="btn-secondary"
                          style={{ fontSize: '10px', padding: '3px 8px' }}
                          onClick={async () => {
                            if (!window.confirm('Regenerate image description with Grok?')) return;
                            showActivity('Regenerating description...');
                            const itemId = detailItem.id;
                            try {
                              const descPreset = descPresets.find(p => p.id === selectedDescPresetId);
                              const res = await axios.post(`${API}/api/ai/describe-image`, {
                                image_url: detailItem.thumbnail_url || detailItem.file_url,
                                vault_item_id: itemId,
                                custom_prompt: descPreset?.custom_prompt || ''
                              });
                              // The VA may have moved to another item meanwhile
                              setDetailItem(prev => (prev && prev.id === itemId ? { ...prev, image_description: res.data.description } : prev));
                              setItems(prev => prev.map(v => v.id === itemId ? { ...v, image_description: res.data.description } : v));
                            } catch (err) {
                              notifyError(err, 'Description failed');
                            }
                            hideActivity();
                          }}
                        >🔄 Regenerate</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#555', fontStyle: 'italic', flex: 1 }}>
                        No description yet.
                      </div>
                      <button
                        style={{ fontSize: '10px', padding: '4px 10px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                        onClick={async () => {
                          showActivity('Generating description...');
                          const itemId = detailItem.id;
                          try {
                            const res = await axios.post(`${API}/api/ai/describe-image`, {
                              image_url: detailItem.thumbnail_url || detailItem.file_url,
                              vault_item_id: itemId
                            });
                            setDetailItem(prev => (prev && prev.id === itemId ? { ...prev, image_description: res.data.description } : prev));
                            setItems(prev => prev.map(v => v.id === itemId ? { ...v, image_description: res.data.description } : v));
                          } catch (err) {
                            notifyError(err, 'Description failed');
                          }
                          hideActivity();
                        }}
                      >👁️ Generate</button>
                    </div>
                  )}
                </div>

                <div style={{ fontSize: '11px', color: '#aaaaaa', marginBottom: '6px' }}>CONTENT TAGS</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                  {contentTags.map(tag => {
                    const active = (detailItem.content_tags || []).includes(tag.name);
                    return (
                      <span
                        key={tag.id}
                        onClick={() => toggleItemTag(tag.name)}
                        style={{
                          padding: '3px 10px', borderRadius: '12px', fontSize: '11px', cursor: 'pointer',
                          background: active ? '#ff4500' : '#2a2a2a',
                          color: active ? '#fff' : '#aaaaaa',
                          border: active ? '1px solid #ff4500' : '1px solid #444'
                        }}
                      >{tag.name}</span>
                    );
                  })}
                  {contentTags.length === 0 && <span style={{ color: '#555', fontSize: '12px' }}>No tags — add some in the Tag Library</span>}
                </div>

                <div style={{ marginTop: 'auto' }}>
                  <button onClick={saveDetail}>Save Changes</button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {detailItem.file_url && (
                <button className="btn-secondary" onClick={() => window.open(detailItem.file_url, '_blank')}>🔍 View</button>
              )}
              {detailItem.file_url && role === 'super_admin' && (
                <>
                <select
                  id="spoof-preset-select"
                  defaultValue="medium"
                  style={{ fontSize: '11px', padding: '4px 6px', background: '#1a1a1a', color: '#f59e0b', border: '1px solid #333', borderRadius: '6px', marginRight: '4px' }}
                >
                  <option value="medium">Medium</option>
                  <option value="high">Hard</option>
                </select>
                <button className="btn-secondary" onClick={async () => {
                  try {
                    const preset = document.getElementById('spoof-preset-select')?.value || 'medium';
                    showActivity(`Spoofing (${preset})...`);
                    const res = await axios.post(`${API}/api/vault/${detailItem.id}/spoof-test`, { preset });

                    if (res.data.queued) {
                      // GIF: poll for completion
                      const target = detailItem; // the item the spoof was started for
                      const startCycle = target.spoof_cycle || 0;
                      const pollInterval = setInterval(async () => {
                        try {
                          const check = await axios.get(`${API}/api/vault/creator/${target.creator_id}?folder_id=${target.folder_id || 'unfiled'}`);
                          const updated = check.data.find(i => i.id === target.id);
                          if (updated && (updated.spoof_cycle || 0) > startCycle) {
                            stopPoller(pollInterval, pollTimeout);
                            hideActivity();
                            // only touch the modal if it still shows this item
                            setDetailItem(prev => (prev && prev.id === updated.id ? updated : prev));
                            setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
                            notify('GIF spoof complete! Cycle: ' + updated.spoof_cycle, 'success');
                          }
                        } catch (e) {}
                      }, 5000);
                      // Timeout after 5 minutes; registered so unmount also clears it
                      const pollTimeout = setTimeout(() => { stopPoller(pollInterval, pollTimeout); hideActivity(); notify('GIF spoof is taking longer than 5 minutes — check the item later.', 'error'); }, 300000);
                      activePollersRef.current.push({ interval: pollInterval, timeout: pollTimeout });
                    } else if (res.data.url) {
                      // Image: immediate download
                      hideActivity();
                      const a = document.createElement('a');
                      a.href = res.data.url;
                      a.download = `spoof_cycle${detailItem.spoof_cycle}_${detailItem.original_filename}`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      const spoofedId = detailItem.id;
                      setDetailItem(prev => (prev && prev.id === spoofedId ? { ...prev, spoof_cycle: res.data.new_cycle } : prev));
                      setItems(prev => prev.map(item =>
                        item.id === spoofedId ? { ...item, spoof_cycle: res.data.new_cycle } : item
                      ));
                    }
                  } catch (e) {
                    hideActivity();
                    notifyError(e, 'Spoof test failed');
                  }
                }}>🧪 Spoof & Download</button>
                </>
              )}

              {detailItem.file_url && detailItem.media_type !== 'gif' && detailItem.media_type !== 'video' && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <button className="btn-secondary" title="Darken this image" style={{ fontSize: '13px', padding: '4px 10px' }} onClick={() => applyBrightness(-1, [detailItem.id])}>🌙 −</button>
                  <select value={brightnessPct} onChange={e => setBrightnessPct(parseInt(e.target.value))}
                    style={{ fontSize: '12px', padding: '3px 4px', background: '#1a1a1a', color: '#ddd', border: '1px solid #333', borderRadius: '6px' }}>
                    {[5, 10, 15, 20, 25, 30].map(p => <option key={p} value={p}>{p}%</option>)}
                  </select>
                  <button className="btn-secondary" title="Brighten this image" style={{ fontSize: '13px', padding: '4px 10px' }} onClick={() => applyBrightness(1, [detailItem.id])}>☀️ +</button>
                </span>
              )}
              {detailItem.status === 'available' && (
                <button className="btn-secondary" onClick={() => updateStatus(detailItem.id, 'rejected')}>Reject</button>
              )}
              {detailItem.status === 'rejected' && (
                <button className="btn-secondary" onClick={() => updateStatus(detailItem.id, 'available')}>Restore</button>
              )}
              {detailItem.status === 'drafted' && (
                <button className="btn-secondary" onClick={() => updateStatus(detailItem.id, 'available')}>Release</button>
              )}
              {(detailItem.status === 'available' || detailItem.status === 'rejected') && (
                <button className="btn-danger" onClick={() => { if (window.confirm('Delete permanently?')) deleteItem(detailItem.id); }}>Delete</button>
              )}
              <button className="btn-secondary" style={{ marginLeft: 'auto' }} onClick={() => setDetailItem(null)}>Close</button>
            </div>

            {/* Usage history */}
            <div>
              {!showUsageHistory ? (
                <button
                  className="btn-secondary"
                  style={{ fontSize: '11px', padding: '4px 12px' }}
                  onClick={() => { setShowUsageHistory(true); fetchItemHistory(detailItem.id); }}
                >📊 Show usage history</button>
              ) : detailHistory.length > 0 ? (
                <div>
                  <div style={{ fontSize: '12px', color: '#aaaaaa', marginBottom: '8px', fontWeight: '600' }}>USAGE HISTORY</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {detailHistory.map((h, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#555', padding: '6px 10px', background: '#0f0f0f', borderRadius: '6px' }}>
                        <span>{h.subreddits?.name || 'Unknown subreddit'}</span>
                        <span>{h.scheduled_sessions?.name || ''}</span>
                        <span>{new Date(h.used_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '11px', color: '#555' }}>No usage history for this item.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Click-to-enlarge lightbox for the detail preview */}
      {detailItem && detailZoom && (
        <div onClick={() => setDetailZoom(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: '20px' }}>
          <img src={(detailItem.file_url || detailItem.thumbnail_url) + (imgBust ? `?v=${imgBust}` : '')} alt=""
            onError={(e) => { if (detailItem.thumbnail_url && e.target.src.indexOf(detailItem.thumbnail_url) === -1) e.target.src = detailItem.thumbnail_url; }}
            style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain', borderRadius: '8px' }} />
        </div>
      )}

      {/* AI Description Settings Modal */}
      {showDescSettings && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowDescSettings(false)}>
          <div style={{ background: '#1a1a1a', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>👁️ AI Image Description Settings</h2>
              <button onClick={() => setShowDescSettings(false)} style={{ background: 'transparent', border: 'none', color: '#aaaaaa', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <p style={{ fontSize: '12px', color: '#555', marginBottom: '16px' }}>
              Configure the prompt Grok uses to describe images. The description is then used for title generation.
            </p>

            {descPresets.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '11px', color: '#aaaaaa', marginBottom: '8px' }}>SAVED PRESETS ({descPresets.length}/5)</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {descPresets.map(preset => (
                    <div key={preset.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#0f0f0f', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '10px 12px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#fff' }}>{preset.name}</div>
                        <div style={{ fontSize: '10px', color: '#555', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {preset.custom_prompt || 'Default Grok prompt'}
                        </div>
                      </div>
                      <button className="btn-secondary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => {
                        setEditingDescPreset(preset);
                        setDescPresetForm({ name: preset.name, custom_prompt: preset.custom_prompt || '' });
                        setShowDescPresetForm(true);
                      }}>Edit</button>
                      <button className="btn-danger" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={async () => {
                        if (!window.confirm('Delete this preset?')) return;
                        try {
                          await axios.delete(`${API}/api/joycaption-presets/${preset.id}`);
                          if (selectedDescPresetId === preset.id) setSelectedDescPresetId('');
                          fetchDescPresets();
                        } catch (err) { notifyError(err, 'Could not delete preset'); }
                      }}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!showDescPresetForm && descPresets.length < 5 && (
              <button className="btn-secondary" style={{ fontSize: '12px', marginBottom: '16px' }} onClick={() => {
                setEditingDescPreset(null);
                setDescPresetForm({ name: '', custom_prompt: '' });
                setShowDescPresetForm(true);
              }}>+ New Preset</button>
            )}

            {showDescPresetForm && (
              <div style={{ background: '#0f0f0f', borderRadius: '8px', padding: '14px', border: '1px solid #2a2a2a', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '600', marginBottom: '12px' }}>
                  {editingDescPreset ? `Edit: ${editingDescPreset.name}` : 'Create New Preset'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <input
                    placeholder="Preset name (e.g. Detailed NSFW)"
                    value={descPresetForm.name}
                    onChange={e => setDescPresetForm(prev => ({ ...prev, name: e.target.value }))}
                    style={{ fontSize: '13px' }}
                  />
                  <div>
                    <label style={{ fontSize: '10px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>DESCRIPTION PROMPT (leave empty for default)</label>
                    <textarea
                      value={descPresetForm.custom_prompt}
                      onChange={e => setDescPresetForm(prev => ({ ...prev, custom_prompt: e.target.value }))}
                      placeholder="Write a detailed description of this image focusing on outfit, pose, and mood."
                      rows={4}
                      style={{ width: '100%', fontSize: '12px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button disabled={!descPresetForm.name.trim()} onClick={async () => {
                      try {
                        if (editingDescPreset) {
                          await axios.put(`${API}/api/joycaption-presets/${editingDescPreset.id}`, descPresetForm);
                        } else {
                          await axios.post(`${API}/api/joycaption-presets`, { ...descPresetForm, creator_id: selectedCreator?.id });
                        }
                        setShowDescPresetForm(false);
                        setEditingDescPreset(null);
                        fetchDescPresets();
                      } catch (err) { alert(err.response?.data?.error || 'Error saving preset'); }
                    }}>{editingDescPreset ? 'Update' : 'Save Preset'}</button>
                    <button className="btn-secondary" onClick={() => { setShowDescPresetForm(false); setEditingDescPreset(null); }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            <div style={{ background: '#0a0a1a', borderRadius: '8px', padding: '12px', border: '1px solid #1d4ed8' }}>
              <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: '600', marginBottom: '6px' }}>How It Works</div>
              <div style={{ fontSize: '11px', color: '#aaaaaa', lineHeight: '1.7' }}>
                <p style={{ marginBottom: '6px' }}>Grok vision analyzes each image and writes a text description. This description is cached and reused for title generation.</p>
                <p style={{ marginBottom: '6px' }}>The <b>default prompt</b> produces general descriptions. Create presets with custom prompts to control the style and focus.</p>
                <p>Select a preset in the vault detail view before clicking Generate or Regenerate.</p>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button className="btn-secondary" onClick={() => setShowDescSettings(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Move to Folder Modal */}
      {showMoveModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 1002,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowMoveModal(false)}>
          <div style={{
            background: '#1a1a1a', borderRadius: '12px', padding: '24px',
            width: '90%', maxWidth: '400px'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 16px 0' }}>Move {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} to...</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '400px', overflow: 'auto' }}>
              <div
                onClick={async () => {
                  try {
                    await axios.post(`${API}/api/vault/folders/move`, { item_ids: selectedItems, folder_id: null });
                    setItems(prev => prev.map(i => selectedItems.includes(i.id) ? { ...i, folder_id: null } : i));
                    fetchFolders();
                    setShowMoveModal(false);
                    setSelectedItems([]);
                    setSelectMode(false);
                  } catch (err) { alert('Error moving items'); }
                }}
                style={{
                  padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                  background: '#0f0f0f', border: '1px solid #2a2a2a',
                  display: 'flex', alignItems: 'center', gap: '10px'
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; }}
              >
                <span style={{ fontSize: '18px' }}>📂</span>
                <span style={{ fontSize: '13px', color: '#aaaaaa' }}>Unfiled</span>
              </div>
              {folders.map(folder => (
                <div
                  key={folder.id}
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/api/vault/folders/move`, { item_ids: selectedItems, folder_id: folder.id });
                      setItems(prev => prev.map(i => selectedItems.includes(i.id) ? { ...i, folder_id: folder.id } : i));
                      fetchFolders();
                      setShowMoveModal(false);
                      setSelectedItems([]);
                      setSelectMode(false);
                    } catch (err) { alert('Error moving items'); }
                  }}
                  style={{
                    padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                    background: '#0f0f0f', border: '1px solid #2a2a2a',
                    display: 'flex', alignItems: 'center', gap: '10px'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; }}
                >
                  <span style={{ fontSize: '18px' }}>📁</span>
                  <div>
                    <div style={{ fontSize: '13px', color: '#fff' }}>{folder.name}</div>
                    <div style={{ fontSize: '10px', color: '#555' }}>{folder.item_count} items</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button className="btn-secondary" onClick={() => setShowMoveModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      


    </div>
  );
}

export default Vault;